import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined } from '@ant-design/icons'
import { Table, Card, Space, Typography, Tooltip, Button } from 'antd'
import { fmtDateTime, formatDuration, formatNumber } from '../../utils'
import type { ShadowWorkflowExecution } from '../../types'
import { statusTag } from './status-tag'

interface LastExecutionsTableProps {
  data: ShadowWorkflowExecution[]
  loading: boolean
  onReload: () => void
}

export const LastExecutionsTable: React.FC<LastExecutionsTableProps> = ({
  data,
  loading,
  onReload
}) => {
  const columns: ColumnsType<ShadowWorkflowExecution> = [
    {
      title: 'Execution',
      key: 'execution',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <Space>
            <Typography.Text strong>#{r.id}</Typography.Text>
            {statusTag(r.status)}
          </Space>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            started {fmtDateTime(r.started_at)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, r) => (
        <Space direction='vertical' size={0}>
          <Typography.Text>{formatDuration(r.duration_ms)}</Typography.Text>
          {r.ended_at && (
            <Typography.Text type='secondary' style={{ fontSize: 12 }}>
              ended {fmtDateTime(r.ended_at)}
            </Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: 'Files Processed',
      key: 'files',
      render: (_, r) => {
        if (!r.files_details || r.files_details.length === 0) {
          return (
            <Typography.Text type='secondary'>No files (found: {r.files_found})</Typography.Text>
          )
        }

        return (
          <Space direction='vertical' size={2}>
            {r.files_details.map((file, idx) => (
              <Tooltip
                key={idx}
                title={
                  <div>
                    <div>
                      <strong>Source:</strong> {file.source_path}
                    </div>
                    <div>
                      <strong>Destination:</strong> {file.destination_path}
                    </div>
                    <div>
                      <strong>Records:</strong> {formatNumber(file.records_count)}
                    </div>
                  </div>
                }
              >
                <Typography.Text code style={{ cursor: 'pointer', display: 'block' }}>
                  {file.name}
                </Typography.Text>
              </Tooltip>
            ))}
          </Space>
        )
      }
    },
    {
      title: 'Records Loaded',
      key: 'records',
      render: (_, r) => {
        const records = [
          { label: 'Providers', value: r.providers_records },
          { label: 'Voids', value: r.checkvoids_records },
          { label: 'Divisions', value: r.epaygrpdivisions_records },
          { label: 'Excl Providers', value: r.exclproviders_records },
          { label: 'EPay Excl', value: r.epayexclproviders_records }
        ].filter(item => item.value > 0)

        if (records.length === 0) {
          return <Typography.Text type='secondary'>—</Typography.Text>
        }

        return (
          <Space direction='vertical' size={2}>
            {records.map(({ label, value }) => (
              <Typography.Text key={label}>
                {label}: <Typography.Text code>{formatNumber(value)}</Typography.Text>
              </Typography.Text>
            ))}
          </Space>
        )
      }
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text type='danger'>{v}</Typography.Text>
          </Tooltip>
        ) : (
          '—'
        )
    }
  ]

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Last 5 Shadow Workflow Executions (with processed files)
          </Typography.Title>
          <Tooltip title='Reload'>
            <Button icon={<ReloadOutlined />} onClick={onReload} size='small' />
          </Tooltip>
        </Space>
      }
      className='rounded-2xl'
    >
      <Table<ShadowWorkflowExecution>
        rowKey={r => r.id}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={false}
        size='middle'
      />
    </Card>
  )
}
